import {Platform} from 'react-native'

import {IS_NATIVE, IS_WEB} from '#/env'

export type AnalyticsSurface = 'web' | 'native'
export type AnalyticsOsType = 'ios' | 'android' | 'web'
export type AnalyticsWebRuntime = 'pwa' | 'browser' | 'n/a'

export type AnalyticsPlatformContext = {
  surface: AnalyticsSurface
  osType: AnalyticsOsType
  webRuntime: AnalyticsWebRuntime
  isPwa: boolean
}

type MatchMedia = (query: string) => {matches: boolean}

type PlatformContextOptions = {
  isWeb?: boolean
  isNative?: boolean
  platformOs?: string
  matchMedia?: MatchMedia
  standalone?: boolean
}

export function detectWebPwaRuntime({
  matchMedia,
  standalone,
}: Pick<PlatformContextOptions, 'matchMedia' | 'standalone'> = {}): boolean {
  const hasStandaloneDisplayMode = Boolean(
    matchMedia?.('(display-mode: standalone)').matches,
  )
  return hasStandaloneDisplayMode || Boolean(standalone)
}

export function getAnalyticsPlatformContext(
  options: PlatformContextOptions = {},
): AnalyticsPlatformContext {
  const isWeb = options.isWeb ?? IS_WEB
  const isNative = options.isNative ?? IS_NATIVE
  const platformOs = options.platformOs ?? Platform.OS

  if (isWeb) {
    const matchMedia =
      options.matchMedia ??
      (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia.bind(window)
        : undefined)
    const standalone =
      options.standalone ??
      (typeof navigator !== 'undefined'
        ? Boolean((navigator as Navigator & {standalone?: boolean}).standalone)
        : false)
    const isPwa = detectWebPwaRuntime({matchMedia, standalone})

    return {
      surface: 'web',
      osType: 'web',
      webRuntime: isPwa ? 'pwa' : 'browser',
      isPwa,
    }
  }

  const osType: AnalyticsOsType =
    platformOs === 'ios' ? 'ios' : platformOs === 'android' ? 'android' : 'web'

  return {
    surface: isNative ? 'native' : 'web',
    osType,
    webRuntime: 'n/a',
    isPwa: false,
  }
}
